import React, { useState, useEffect, useMemo } from 'react';
import { useSearchParams, Navigate, useNavigate } from 'react-router-dom';
import { useUser } from '../../context/userContext';
import { CircularProgress } from '@mui/material';
import DashboardSlideshow from './dashboardSlideshow';
import TopAgentsChart from './topAgentsChart';
import RecentSalesTable from './recentSalesTable';
import BestMonthlyLeaderboard from './bestMonthlyLeaderboard';
import DashboardNoticeBoard from './dashboardNoticeBoard';
import CompanyGoalBar, { SALES_GOAL } from './companyGoalBar';
import { getAllSales, getAllSalesByAgentId, getRecentSales, getTopAgents, getCompanyGoalTotal } from '../../api/salesApi';
import { getCommissionProgressByAgentId } from '../../api/commissionProgressApi';
import { getAllNotices } from '../../api/noticeApi';
import { JoinGroupButtons } from '../common/joinGroups';

export const NewDashboard = () => {
    const { user } = useUser();
    const navigate = useNavigate();
    const [searchParams] = useSearchParams();
    const autoOpenNoticeId = parseInt(searchParams.get('noticeId')) || null;
    const [loading, setLoading] = useState(true);
    const [sales, setSales] = useState([]);
    const [commissions, setCommissions] = useState([]);
    const [recentSales, setRecentSalesData] = useState([]);
    const [notices, setNotices] = useState([]);
    const [topRank, setTopRank] = useState(0); // 0 = not in top 5, 1-5 = rank
    const [companyGoalTotal, setCompanyGoalTotal] = useState(0);
    // Shared period: the Top Agents selector also drives the Total Sales Revenue card.
    const [selMonth, setSelMonth] = useState(new Date().getMonth() + 1);
    const [selYear, setSelYear] = useState(new Date().getFullYear());

    const userId = user?.userId;
    // Referral (and only Referral) has no dashboard — send them to the commission side.
    const isReferralOnly = (user?.roles?.length ?? 0) > 0 && user.roles.every(r => r === 'Referral');
    // Astern Admin has no dashboard — send them to All Sales even on a direct URL.
    const isAsternAdmin = user?.roles?.includes('Astern Admin');
    const canViewAll = ['Super Admin', 'Admin', 'Accountant'].some(r =>
        user?.roles?.includes(r)
    );
    // Pure admins earn no commission — hide the personal commission cards for them.
    // (A multi-role admin who ALSO earns, e.g. Super Admin + Leader, still sees them.)
    const isPureAdmin = (user?.roles?.length ?? 0) > 0 &&
        user.roles.every(r => ['Admin', 'Astern Admin', 'Group Admin', 'Super Admin'].includes(r));

    useEffect(() => {
        const fetchData = async () => {
            try {
                setLoading(true);

                const now = new Date();
                const [salesRes, commRes, recentRes, noticeRes, topRes] = await Promise.all([
                    canViewAll ? getAllSales() : getAllSalesByAgentId(userId),
                    getCommissionProgressByAgentId(userId),
                    getRecentSales(10),
                    getAllNotices(),
                    getTopAgents(now.getMonth() + 1, now.getFullYear()),
                ]);

                setSales(salesRes.data || []);
                setCommissions(commRes.data || []);
                setRecentSalesData(recentRes.data || []);
                setNotices(noticeRes.data || []);
                const topList = topRes.data || [];
                const rankIdx = topList.findIndex(a => String(a.userId) === String(userId));
                setTopRank(rankIdx >= 0 ? rankIdx + 1 : 0);
            } catch (err) {
                console.error('Dashboard fetch error:', err);
            }

            // Company goal total — isolated so a failure here never blanks the dashboard.
            try {
                const goalRes = await getCompanyGoalTotal(SALES_GOAL.year);
                setCompanyGoalTotal(Number(goalRes.data?.total) || 0);
            } catch (err) {
                console.error('Company goal total fetch error:', err);
            }

            setLoading(false);
        };
        if (userId) fetchData();
    }, [userId, canViewAll]);

    const metrics = useMemo(() => {
        const monthStart = new Date(selYear, selMonth - 1, 1);
        const monthEnd = new Date(selYear, selMonth, 1);

        // Revenue counts ACTIVE sales only — Pending (not yet closed) and Refunded are
        // excluded, matching the /rankings leaderboard rule (Status != Pending/Refunded,
        // Import kept). Prevents the tile + Top 5 from inflating with unclosed deals.
        const monthSales = sales.filter(s => {
            const d = new Date(s.depositOn);
            if (!(d >= monthStart && d < monthEnd)) return false;
            return s.status !== 'Pending' && s.status !== 'Refunded';
        });
        // Co-broke 50/50 (user rule 2026-06-07): on the PERSONAL card a sale with a
        // co-agent counts half for each side — same rule as Top Agents + the WhatsApp
        // leaderboard, so all surfaces tally. Admin view (canViewAll) sums company-wide
        // totals where each sale appears once, so it stays at full price.
        const saleShare = (s) => {
            // Revenue = full amount the customer pays: FinalPrice (system-only,
            // standardized 2026-06-08) + AddOnPrice (battery + add-ons).
            const v = (s.finalPrice || 0) + (s.addOnPrice || 0);
            return (!canViewAll && s.coAgentDetails) ? v / 2 : v;
        };
        const totalSalesRevenue = monthSales.reduce((sum, s) => sum + saleShare(s), 0);
        const activeSales = sales.filter(s => s.status === 'Active' || s.status === 'Pending').length;
        const pendingClosure = sales.filter(s => s.status === 'Pending').length;

        const totalPaid = commissions
            .filter(c => c.paymentStatus === 'Completed')
            .reduce((sum, c) => sum + (c.commPay || 0), 0);
        const totalPending = commissions
            .filter(c => c.paymentStatus !== 'Completed')
            .reduce((sum, c) => sum + (c.commPay || 0), 0);

        return { totalSalesRevenue, totalPaid, totalPending, activeSales, pendingClosure };
    }, [sales, commissions, canViewAll, selMonth, selYear]);

    const MONTH_NAMES = ['Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun', 'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec'];
    const periodLabel = `${MONTH_NAMES[selMonth - 1]} ${selYear}`;

    const noticeboardItems = notices.filter(n => n.type === 'Noticeboard' || (!n.type && !n.showInSlideshow));

    if (isReferralOnly) return <Navigate to="/commissions/referral" replace />;
    if (isAsternAdmin) return <Navigate to="/accounting/sales" replace />;

    if (loading) {
        return (
            <div style={{ display: 'flex', justifyContent: 'center', alignItems: 'center', height: '60vh' }}>
                <CircularProgress />
            </div>
        );
    }

    const greeting = () => {
        const hour = new Date().getHours();
        if (hour < 12) return 'Good morning';
        if (hour < 18) return 'Good afternoon';
        return 'Good evening';
    };

    const formatDate = () => {
        return new Date().toLocaleDateString('en-GB', {
            weekday: 'short', day: 'numeric', month: 'short', year: 'numeric'
        });
    };

    const formatRM = (val) => `RM ${(val || 0).toLocaleString('en-MY', { minimumFractionDigits: 0, maximumFractionDigits: 0 })}`;

    return (
        <div className="dash-container">
            {/* Company sales goal — 2026 toward RM 100M, visible to everyone */}
            <CompanyGoalBar total={companyGoalTotal} goal={SALES_GOAL.goal} targets={SALES_GOAL.targets} />

            {/* Quick shortcuts — simple square tiles; more can be added beside RSC */}
            <div style={{ display: 'flex', flexWrap: 'wrap', gap: 10, marginBottom: 20 }}>
                <div
                    onClick={() => navigate('/tools/rsc')}
                    title="Residential Solar Calculator"
                    style={{ display: 'flex', alignItems: 'center', justifyContent: 'center', width: 72, height: 72, cursor: 'pointer', background: '#0F62CC', color: '#fff', borderRadius: 12, fontWeight: 700, fontSize: 15, letterSpacing: 0.5, transition: 'transform .12s, box-shadow .12s' }}
                    onMouseEnter={e => { e.currentTarget.style.boxShadow = '0 4px 14px rgba(15,98,204,0.28)'; e.currentTarget.style.transform = 'translateY(-2px)'; }}
                    onMouseLeave={e => { e.currentTarget.style.boxShadow = 'none'; e.currentTarget.style.transform = 'none'; }}
                >
                    RSC
                </div>
                {/* Sales community, beside RSC so everyone can find the groups. Same two links
                    as the Tutorials card and the welcome banner. (user 2026-09-25)

                    alwaysShow: these two STAY once the welcome banner has been dismissed. The X
                    silences the nagging banner at the top of every page; it should not also take
                    away the only permanent way to reach the groups, which is what happened — the
                    links vanished for good and had to be dug out of localStorage.
                    (user 2026-10-05: "where is the telegram and whatsapp icon") */}
                <JoinGroupButtons alwaysShow />
            </div>

            {/* Greeting Header */}
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 20 }}>
                <div>
                    {topRank > 0 ? (
                        <>
                            <h2 style={{ margin: 0, fontSize: 22, fontWeight: 700 }}>Congrats, {user?.firstName || user?.alias}! 🎉</h2>
                            <p style={{ margin: '4px 0 0', color: '#F59E0B', fontSize: 13, fontWeight: 500 }}>You are Top {topRank} Sales this month!</p>
                        </>
                    ) : (
                        <>
                            <h2 style={{ margin: 0, fontSize: 22, fontWeight: 700 }}>Hi, {user?.firstName || user?.alias}! Enjoy Earning!</h2>
                            <p style={{ margin: '4px 0 0', color: '#94A3B8', fontSize: 13 }}>Here's your sales overview</p>
                        </>
                    )}
                </div>
                <div style={{ background: '#f1f5f9', borderRadius: 8, padding: '6px 14px', fontSize: 13, color: '#64748B' }}>
                    {formatDate()}
                </div>
            </div>

            {/* Row 1: Slideshow + Noticeboard — stack on mobile */}
            <div className="dash-row-1">
                <div className="dash-slideshow">
                    <DashboardSlideshow />
                </div>
                <div className="dash-noticeboard">
                    <DashboardNoticeBoard notices={noticeboardItems} autoOpenNoticeId={autoOpenNoticeId} />
                </div>
            </div>

            {/* Row 2: Summary Cards — 2 per row on mobile */}
            <div className={`dash-cards${isPureAdmin ? ' dash-cards--admin' : ''}`}>
                <MetricCard label="TOTAL SALES REVENUE" value={formatRM(metrics.totalSalesRevenue)} sub={periodLabel} subColor="#1A8CFF" iconBg="rgba(26,140,255,0.1)" icon="↗" iconColor="#1A8CFF" />
                {!isPureAdmin && <MetricCard label="COMMISSIONS PAID" value={formatRM(metrics.totalPaid)} sub="Completed" subColor="#10B981" iconBg="rgba(16,185,129,0.1)" icon="$" iconColor="#10B981" />}
                {!isPureAdmin && <MetricCard label="PENDING COMMISSION" value={formatRM(metrics.totalPending)} sub="Awaiting approval" subColor="#F59E0B" iconBg="rgba(245,158,11,0.1)" icon="↗" iconColor="#F59E0B" />}
                <MetricCard label="ACTIVE SALES" value={metrics.activeSales} sub={`${metrics.pendingClosure} Pending Closure`} subColor="#94A3B8" iconBg="rgba(139,92,246,0.1)" icon="👥" iconColor="#8B5CF6" />
            </div>

            {/* Row 3: Top 5 + Recent Sales — stack on mobile */}
            <div className="dash-row-3">
                <div className="dash-top-agents">
                    <TopAgentsChart month={selMonth} year={selYear} onPeriodChange={(m, y) => { setSelMonth(m); setSelYear(y); }} />
                </div>
                <div className="dash-recent-sales">
                    <RecentSalesTable sales={recentSales} />
                </div>
            </div>

            {/* Row 4: Best all-time monthly sales leaderboard — half width (the bars ran
                edge-to-edge once the dashboard went full-width). Full width on mobile. */}
            <div className="dash-leaderboard">
                <BestMonthlyLeaderboard />
            </div>

            <style>{`
                .dash-container { max-width: none; margin: 0; overflow-x: hidden; }
                .dash-row-1 { display: flex; gap: 16px; margin-bottom: 20px; align-items: stretch; }
                .dash-slideshow { flex: 7; }
                .dash-noticeboard { flex: 3; }
                .dash-cards { display: grid; grid-template-columns: repeat(4, 1fr); gap: 16px; margin-bottom: 20px; }
                .dash-cards--admin { grid-template-columns: repeat(2, 1fr); }
                .dash-row-3 { display: flex; gap: 16px; align-items: stretch; }
                .dash-top-agents { flex: 4; }
                .dash-recent-sales { flex: 6; overflow-x: auto; }
                /* Make the two Row-3 cards tally (equal height). */
                .dash-top-agents > div, .dash-recent-sales > div { height: 100%; box-sizing: border-box; }
                .dash-leaderboard { max-width: 58%; margin-top: 20px; }

                @media (max-width: 768px) {
                    .dash-container { overflow-x: hidden; }
                    .dash-row-1 { flex-direction: column; }
                    .dash-slideshow, .dash-noticeboard { flex: none; width: 100%; }
                    .dash-cards { grid-template-columns: repeat(2, 1fr); gap: 10px; }
                    .dash-row-3 { flex-direction: column; }
                    .dash-top-agents, .dash-recent-sales { flex: none; width: 100%; }
                    .dash-recent-sales table { min-width: 500px; }
                    .dash-leaderboard { max-width: 100%; }
                }
            `}</style>
        </div>
    );
};

const MetricCard = ({ label, value, sub, subColor, iconBg, icon, iconColor }) => (
    <div style={{ background: 'white', borderRadius: 12, padding: 18, boxShadow: '0 1px 3px rgba(0,0,0,0.06)' }}>
        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'start' }}>
            <div>
                <div style={{ color: '#94A3B8', fontSize: 10, fontWeight: 600, letterSpacing: 1 }}>{label}</div>
                <div style={{ fontSize: 22, fontWeight: 700, marginTop: 8 }}>{value}</div>
                <div style={{ color: subColor, fontSize: 12, marginTop: 4 }}>{sub}</div>
            </div>
            <div style={{ background: iconBg, borderRadius: 8, width: 36, height: 36, display: 'flex', alignItems: 'center', justifyContent: 'center', color: iconColor, fontSize: 16 }}>
                {icon}
            </div>
        </div>
    </div>
);

export default NewDashboard;
